import { RootState } from "@app/store";
import CloseIcon from "@mui/icons-material/Close";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import Drawer from "@mui/material/Drawer";
import {
  backgroundColors,
  dividerColors,
  paperColors,
  primaryColors,
  secondaryColors,
  textColors,
} from "@themes/colorScheme";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setDefaultBackgroundColor,
  setDividerColor,
  setPaperBackgroundColor,
  setPrimaryColor,
  setSecondaryColor,
  setTextColor,
} from "src/features/colorPaletteSlice";
import useThemeToggle from "src/hook/useThemeToggle";

export default function Settings() {
  const dispatch = useDispatch();
  const { lightPalette, darkPalette } = useSelector(
    (state: RootState) => state.colorPalette
  );
  const { setTheme, mode } = useThemeToggle();
  const [open, setOpen] = React.useState(false);
  const [selectedPrimaryColor, setSelectedPrimaryColor] =
    React.useState<string>("");
  const [selectedSecondaryColor, setSelectedSecondaryColor] =
    React.useState<string>("");
  const [selectedBackgroundColor, setSelectedBackgroundColor] =
    React.useState<string>("");
  const [selectedPaperColor, setSelectedPaperColor] =
    React.useState<string>("");
  const [selectedDividerColor, setSelectedDividerColor] =
    React.useState<string>("");
  const [selectedTextColor, setSelectedTextColor] = React.useState<string>("");
  const [isSelectBackgroundColor, setIsSelectBackgroundColor] =
    React.useState<boolean>(false);

  const handleDrawer = () => {
    setOpen(!open);
  };

  // set default color
  React.useEffect(() => {
    if (mode === "light") {
      setSelectedPrimaryColor(lightPalette.primary.main);
      setSelectedSecondaryColor(lightPalette.secondary.main);
      setSelectedBackgroundColor(lightPalette.background.default);
      setSelectedPaperColor(lightPalette.background.paper);
      setSelectedDividerColor(lightPalette.divider);
      setSelectedTextColor(lightPalette.text.primary);
    } else {
      setSelectedPrimaryColor(darkPalette.primary.main);
      setSelectedSecondaryColor(darkPalette.secondary.main);
      setSelectedBackgroundColor(darkPalette.background.default);
      setSelectedPaperColor(darkPalette.background.paper);
      setSelectedDividerColor(darkPalette.divider);
      setSelectedTextColor(darkPalette.text.primary);
    }
  }, [mode, lightPalette, darkPalette]);

  return (
    <React.Fragment>
      <IconButton
        onClick={handleDrawer}
        className="anim-icon-btn"
        sx={{
          color: (theme) => theme.palette.text.primary,
        }}
        title="Settings"
      >
        <SettingsIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawer}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: {
              xs: "85%",
              md: "40%",
            },
            // The palette's `paper` colour is a light grey in dark mode, which
            // left the panel with poor contrast — use the app background.
            backgroundColor: (theme) => theme.palette.background.default,
          },
        }}
      >
        <div className="anim-settings">
          <div className="anim-settings__head">
            <h2 className="anim-settings__title">Settings</h2>
            <IconButton
              onClick={handleDrawer}
              className="anim-icon-btn"
              aria-label="Close settings"
              title="Close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <Stack p={{ xs: 2, md: 3 }} gap={2} sx={{ overflowY: "auto" }}>
            {/* Language selection */}
            <div className="anim-settings__section">
              <FormLabel>Select Language</FormLabel>
              <div id="google_translate_element" style={{ marginTop: 8 }} />
            </div>

            {/* mode selection */}
            <div className="anim-settings__section">
              <FormControl>
                <FormLabel>Select Theme</FormLabel>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => setTheme(e.target.value)}
                >
                  <FormControlLabel
                    value="light"
                    control={<Radio />}
                    label="Light"
                  />
                  <FormControlLabel
                    value="dark"
                    control={<Radio />}
                    label="Dark"
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {/* primary color */}
            <div className="anim-settings__section">
              <FormLabel>Primary Color</FormLabel>
              <div className="anim-swatches">
                {primaryColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-swatch${
                      selectedPrimaryColor === color.main ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: color.main }}
                    title={color.main}
                    aria-label={`Primary colour ${color.main}`}
                    onClick={() => dispatch(setPrimaryColor({ color, mode }))}
                  />
                ))}
              </div>
            </div>

            {/* secondary color */}
            <div className="anim-settings__section">
              <FormLabel>Secondary Color</FormLabel>
              <div className="anim-swatches">
                {secondaryColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-swatch${
                      selectedSecondaryColor === color.main ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: color.main }}
                    title={color.main}
                    aria-label={`Secondary colour ${color.main}`}
                    onClick={() => dispatch(setSecondaryColor({ color, mode }))}
                  />
                ))}
              </div>
            </div>

            {/* background color */}
            <div className="anim-settings__section">
              <FormLabel>Background Color</FormLabel>
              <div className="anim-swatches">
                {backgroundColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-swatch anim-swatch--rect${
                      selectedBackgroundColor === color ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                    aria-label={`Background colour ${color}`}
                    onClick={() => {
                      dispatch(setDefaultBackgroundColor({ color, mode }));
                      setIsSelectBackgroundColor(true);
                    }}
                  />
                ))}
                <label
                  className="anim-swatch anim-swatch--custom"
                  title="Custom background colour"
                >
                  <FormatColorFillIcon fontSize="small" />
                  <input
                    type="color"
                    className="anim-swatch__input"
                    value={selectedBackgroundColor || "#ffffff"}
                    aria-label="Custom background colour"
                    onChange={(e) => {
                      dispatch(
                        setDefaultBackgroundColor({
                          color: e.target.value,
                          mode,
                        })
                      );
                      setIsSelectBackgroundColor(true);
                    }}
                  />
                </label>
              </div>
              <Collapse in={isSelectBackgroundColor}>
                <Typography
                  textAlign={"center"}
                  variant="caption"
                  color={"error"}
                >
                  To improve visibility, consider adjusting the paper color and
                  text color to ensure the text stands out against the
                  background.
                </Typography>
              </Collapse>
            </div>

            {/* paper color */}
            <div className="anim-settings__section">
              <FormLabel>Paper Color</FormLabel>
              <div className="anim-swatches">
                {paperColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-swatch anim-swatch--rect${
                      selectedPaperColor === color ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                    aria-label={`Paper colour ${color}`}
                    onClick={() =>
                      dispatch(setPaperBackgroundColor({ color, mode }))
                    }
                  />
                ))}
                <label
                  className="anim-swatch anim-swatch--custom"
                  title="Custom paper colour"
                >
                  <FormatColorFillIcon fontSize="small" />
                  <input
                    type="color"
                    className="anim-swatch__input"
                    value={selectedPaperColor || "#ffffff"}
                    aria-label="Custom paper colour"
                    onChange={(e) =>
                      dispatch(
                        setPaperBackgroundColor({
                          color: e.target.value,
                          mode,
                        })
                      )
                    }
                  />
                </label>
              </div>
            </div>

            {/* divider color */}
            <div className="anim-settings__section">
              <FormLabel>Divider Color</FormLabel>
              <div className="anim-swatches">
                {dividerColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-swatch anim-swatch--rect${
                      selectedDividerColor === color ? " is-selected" : ""
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                    aria-label={`Divider colour ${color}`}
                    onClick={() => dispatch(setDividerColor({ color, mode }))}
                  />
                ))}
              </div>
            </div>

            {/* text color */}
            <div className="anim-settings__section">
              <FormLabel>Text Color</FormLabel>
              <Stack gap={1} mt={1.5}>
                {textColors.map((color, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`anim-textoption${
                      selectedTextColor === color.primary ? " is-selected" : ""
                    }`}
                    aria-label={`Text colour preset ${index + 1}`}
                    onClick={() => {
                      dispatch(setTextColor({ color, mode }));
                      setIsSelectBackgroundColor(false);
                    }}
                  >
                    <span
                      className="anim-textoption__dot"
                      style={{ backgroundColor: color.primary }}
                    />
                    <span style={{ color: color.primary }}>Primary</span>
                    <span style={{ color: color.secondary }}>Secondary</span>
                    <span style={{ color: color.disabled }}>Disabled</span>
                  </button>
                ))}
              </Stack>
            </div>
          </Stack>
        </div>
      </Drawer>
    </React.Fragment>
  );
}
